// API-Helper. Holt CSRF-Token aus Response-Header und speichert im Memory.
let csrfToken: string | null = null;

async function getCsrf(): Promise<string> {
  if (csrfToken) return csrfToken;
  const res = await fetch('/auth/me', { credentials: 'same-origin' });
  const t = res.headers.get('x-csrf-token');
  if (t) csrfToken = t;
  return csrfToken ?? '';
}

export async function api<T = unknown>(
  path: string,
  opts: RequestInit = {},
): Promise<T> {
  const method = (opts.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = {
    ...(opts.headers as Record<string, string> | undefined),
  };
  // content-type nur bei tatsächlichem Body — sonst beschwert sich Fastify's JSON-Parser
  if (opts.body != null) {
    headers['content-type'] = 'application/json';
  }
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    headers['x-csrf-token'] = await getCsrf();
  }
  const res = await fetch(path, { ...opts, credentials: 'same-origin', headers });
  if (res.status === 401) {
    window.location.href = '/auth/login';
    throw new Error('unauthenticated');
  }
  const csrf = res.headers.get('x-csrf-token');
  if (csrf) csrfToken = csrf;
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch {}
    throw new ApiError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`api_error_${status}`);
  }
}

export type Me = {
  id: string;
  email: string;
  displayName: string;
  color: string;
  notifyViaMail: boolean;
  notifyViaPush: boolean;
};

export type UserBasic = {
  id: string;
  email: string;
  displayName: string;
  color: string;
};

export type Child = {
  id: string;
  name: string;
  color: string;
  graphCalendarId: string;
  birthdate: string | null;
};

export type Participant = {
  id: string;
  parentEventId: string;
  actorType: 'user' | 'external' | 'child';
  actorUserId: string | null;
  actorChildId: string | null;
  actorExternalName: string | null;
  role: string;
  acceptStatus: 'pending' | 'accepted' | 'declined' | 'auto';
  startsAtAnchor: 'event_start' | 'event_end';
  startsAtOffsetMin: number;
  endsAtAnchor: 'event_start' | 'event_end';
  endsAtOffsetMin: number;
  travelMinutes: number | null;
  graphEventId: string | null;
  graphCalendarOwner: string | null;
  participantNotes: string | null;
};

export type ExpandedEvent = {
  parentEventId: string;
  title: string;
  startsAt: string;
  endsAt: string;
  location: string | null;
  notes: string | null;
  childIds: string[];
  recurrenceRule: string | null;
  cancelledAt: string | null;
  unresolvedChildcare: boolean;
  instanceException: { instanceDate: string; action: string } | null;
  participants: Array<Participant & { resolvedSlot: { start: string; end: string } }>;
};

export type EventDetail = {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string;
  location: string | null;
  notes: string | null;
  childIds: string[];
  recurrenceRule: string | null;
  cancelledAt: string | null;
  unresolvedChildcare: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  participants: Participant[];
  exceptions: Array<{
    id: string;
    seriesParentEventId: string;
    instanceDate: string;
    action: string;
    overrideData: unknown;
  }>;
};

export type FamilySettings = {
  id: number;
  familyName: string;
  accentColor: string;
  logoEmoji: string | null;
};
